import { formatDate } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import { toDate } from 'date-fns-tz';

// Removes Timezone from the Date
export function stringToTimeZoneFreeDate(stringDate: string): Date {
  /*
   * need to replace hyphens with slash and remove milliseconds
   * so that the date can be parsed by safari
   */
  const date = new Date(stringDate.substring(0, 10).replace(/-/g, '/').split('.')[0]);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  return date;
}

// returns the data in format mm-dd-yyyy
export function formatDateForCart(dateString: string): string {
  return dateString.split('/')?.join('-');
}

// get date object from string date
export function getDateObject(date: string | Date, timezone: string): Date {
  return typeof date === 'string' ? toDate(date, { timeZone: timezone }) : date;
}

export function formatDateForIBKBackend(date: Date, timezone: string): string {
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone
  };

  // ibk backend expects date format in a specific format so using this locale 'yyyy-mm-dd'
  const IBK_DATE_FORMAT_LOCALE = 'fr-CA';
  return `${formatDate(IBK_DATE_FORMAT_LOCALE, date, dateOptions)}T00:00:00.000Z`;
}

// Output in format June 2022
export function formatDateForReviews(date: Date, locale): string {
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long'
  };

  return `${formatDate(locale, date, dateOptions)}`;
}

/**
 * reverses and replaces / with -
 * input -> 23/02/2022
 * output -> 2022-02-23
 */
export function formatDateForIBK(dateString: string): string {
  return dateString.split('/')?.reverse()?.join('-');
}

/*
 * Formatter that takes a string of the form "YYYY-MM-DD', and removes any 4 digit number and optionally a
 * preceding comma and/or another whitespace character, then eats any symbol that may follow the year directly.
 */
export function stringToYearlessDateFormatter(dateString: string, locale: Locale): string {
  /*
   * Need to split the date into its respective components here as 'YYYY-MM-DD' is interpreted as UTC,
   * causing the date to be off in UTC-X timezones.
   */
  const splitDate = dateString ? dateString.split('-') : [];

  const date = new Date(parseInt(splitDate[0], 10), parseInt(splitDate[1], 10) - 1, parseInt(splitDate[2], 10));
  return splitDate.length === 3
    ? formatDate(locale, date, { year: 'numeric', month: 'short', day: '2-digit' }).replace(
        /,?[^.]?[0-9][0-9][0-9][0-9]?./,
        ''
      )
    : '';
}

export async function loadDateFnsFunctions() {
  const [format, isAfter, isBefore, isValid, parse] = await Promise.all([
    import('date-fns/format'),
    import('date-fns/isAfter'),
    import('date-fns/isBefore'),
    import('date-fns/isValid'),
    import('date-fns/parse')
  ]);

  return {
    format: format.default,
    isAfter: isAfter.default,
    isBefore: isBefore.default,
    isValid: isValid.default,
    parse: parse.default
  };
}
